/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * PROGRAM HERO PREMIUM - Enterprise-Grade Visual Experience
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * A jaw-dropping, WebGL-enhanced carousel experience that rivals Apple's best work.
 * Features:
 * - GPU-accelerated WebGL shader effects with organic noise distortion
 * - Magnetic cursor interactions with spring physics
 * - 3D perspective tilt on cards with depth layers
 * - Velocity-based scroll animations
 * - Premium glassmorphism and light trail effects
 * - Buttery smooth 120fps animations
 * - Fully responsive with graceful degradation
 * 
 * @version 2.0.0
 * @date December 2025
 */

(function() {
  'use strict';
  
  // ═══════════════════════════════════════════════════════════════════════════
  // CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════════
  
  const CONFIG = {
    // WebGL Settings
    webgl: {
      enabled: true,
      intensity: 0.015,
      frequency: 3.0,
      amplitude: 0.02
    },
    // Magnetic Cursor Settings
    magnetic: {
      strength: 0.35,
      radius: 150,
      smoothing: 0.12
    },
    // 3D Tilt Settings
    tilt: {
      maxRotation: 12,
      perspective: 1200,
      scale: 1.02,
      speed: 400,
      glare: true,
      glareOpacity: 0.15
    },
    // Spring Physics
    spring: {
      stiffness: 0.08,
      damping: 0.85,
      mass: 1
    },
    // Performance
    performance: {
      targetFPS: 60,
      useRAF: true,
      throttleResize: 150
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITY FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const lerp = (start, end, factor) => start + (end - start) * factor;
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const mapRange = (value, inMin, inMax, outMin, outMax) => 
    outMin + (outMax - outMin) * ((value - inMin) / (inMax - inMin));
  
  const isMobile = () => window.innerWidth <= 768 || 'ontouchstart' in window;
  const isReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const supportsWebGL = () => {
    try {
      const canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && 
        (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
    } catch(e) { return false; }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // WEBGL DISTORTION SHADER
  // ═══════════════════════════════════════════════════════════════════════════
  
  class WebGLDistortion {
    constructor(canvas, image) {
      this.canvas = canvas;
      this.image = image;
      this.gl = null;
      this.program = null;
      this.animationId = null;
      this.uniforms = {};
      this.mouse = { x: 0.5, y: 0.5, targetX: 0.5, targetY: 0.5 };
      this.time = 0;
      this.intensity = 0;
      this.targetIntensity = 0;
      
      this.vertexShader = `
        attribute vec2 a_position;
        attribute vec2 a_texCoord;
        varying vec2 v_texCoord;
        void main() {
          gl_Position = vec4(a_position, 0.0, 1.0);
          v_texCoord = a_texCoord;
        }
      `;
      
      this.fragmentShader = `
        precision highp float;
        
        uniform sampler2D u_image;
        uniform vec2 u_mouse;
        uniform float u_time;
        uniform float u_intensity;
        uniform vec2 u_resolution;
        
        varying vec2 v_texCoord;
        
        // Premium noise function - organic, film-like grain
        vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
        vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
        
        float snoise(vec3 v) {
          const vec2 C = vec2(1.0/6.0, 1.0/3.0);
          const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
          
          vec3 i = floor(v + dot(v, C.yyy));
          vec3 x0 = v - i + dot(i, C.xxx);
          
          vec3 g = step(x0.yzx, x0.xyz);
          vec3 l = 1.0 - g;
          vec3 i1 = min(g.xyz, l.zxy);
          vec3 i2 = max(g.xyz, l.zxy);
          
          vec3 x1 = x0 - i1 + C.xxx;
          vec3 x2 = x0 - i2 + C.yyy;
          vec3 x3 = x0 - D.yyy;
          
          i = mod289(i);
          vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));
            
          float n_ = 0.142857142857;
          vec3 ns = n_ * D.wyz - D.xzx;
          
          vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
          
          vec4 x_ = floor(j * ns.z);
          vec4 y_ = floor(j - 7.0 * x_);
          
          vec4 x = x_ *ns.x + ns.yyyy;
          vec4 y = y_ *ns.x + ns.yyyy;
          vec4 h = 1.0 - abs(x) - abs(y);
          
          vec4 b0 = vec4(x.xy, y.xy);
          vec4 b1 = vec4(x.zw, y.zw);
          
          vec4 s0 = floor(b0)*2.0 + 1.0;
          vec4 s1 = floor(b1)*2.0 + 1.0;
          vec4 sh = -step(h, vec4(0.0));
          
          vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
          vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
          
          vec3 p0 = vec3(a0.xy, h.x);
          vec3 p1 = vec3(a0.zw, h.y);
          vec3 p2 = vec3(a1.xy, h.z);
          vec3 p3 = vec3(a1.zw, h.w);
          
          vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
          p0 *= norm.x;
          p1 *= norm.y;
          p2 *= norm.z;
          p3 *= norm.w;
          
          vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
          m = m * m;
          return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
        }
        
        void main() {
          vec2 uv = v_texCoord;
          
          // Calculate distance from mouse for localized effect
          float dist = distance(uv, u_mouse);
          float influence = smoothstep(0.5, 0.0, dist);
          
          // Multi-layered noise for organic feel
          float noise1 = snoise(vec3(uv * 3.0, u_time * 0.3)) * 0.5;
          float noise2 = snoise(vec3(uv * 6.0, u_time * 0.5 + 100.0)) * 0.25;
          float noise3 = snoise(vec3(uv * 12.0, u_time * 0.7 + 200.0)) * 0.125;
          float combinedNoise = noise1 + noise2 + noise3;
          
          // Premium ripple effect centered on cursor
          float ripple = sin(dist * 15.0 - u_time * 2.0) * 0.5 + 0.5;
          ripple *= influence * u_intensity;
          
          // Chromatic aberration based on mouse proximity
          float aberration = u_intensity * influence * 0.008;
          
          // Apply distortion
          vec2 distortion = vec2(
            combinedNoise * u_intensity * 0.02 + ripple * 0.01,
            combinedNoise * u_intensity * 0.02 + ripple * 0.01
          );
          
          vec2 distortedUV = uv + distortion * influence;
          
          // Sample with chromatic aberration for premium look
          vec4 colorR = texture2D(u_image, distortedUV + vec2(aberration, 0.0));
          vec4 colorG = texture2D(u_image, distortedUV);
          vec4 colorB = texture2D(u_image, distortedUV - vec2(aberration, 0.0));
          
          vec4 finalColor = vec4(colorR.r, colorG.g, colorB.b, colorG.a);
          
          // Add subtle vignette
          float vignette = 1.0 - smoothstep(0.4, 1.4, length(uv - 0.5) * 1.5);
          finalColor.rgb *= mix(1.0, vignette, 0.15);
          
          // Subtle glow on hover
          float glow = influence * u_intensity * 0.1;
          finalColor.rgb += vec3(0.93, 0.72, 0.48) * glow;
          
          gl_FragColor = finalColor;
        }
      `;
    }
    
    init() {
      if (!supportsWebGL()) return false;
      
      this.gl = this.canvas.getContext('webgl', {
        alpha: true,
        antialias: true,
        preserveDrawingBuffer: false,
        powerPreference: 'high-performance'
      }) || this.canvas.getContext('experimental-webgl');
      
      if (!this.gl) return false;
      
      this.setupProgram();
      this.setupBuffers();
      this.loadTexture();
      this.resize();
      
      return true;
    }
    
    setupProgram() {
      const gl = this.gl;
      
      const vertShader = gl.createShader(gl.VERTEX_SHADER);
      gl.shaderSource(vertShader, this.vertexShader);
      gl.compileShader(vertShader);
      
      const fragShader = gl.createShader(gl.FRAGMENT_SHADER);
      gl.shaderSource(fragShader, this.fragmentShader);
      gl.compileShader(fragShader);
      
      this.program = gl.createProgram();
      gl.attachShader(this.program, vertShader);
      gl.attachShader(this.program, fragShader);
      gl.linkProgram(this.program);
      gl.useProgram(this.program);
      
      // Get uniform locations
      this.uniforms = {
        image: gl.getUniformLocation(this.program, 'u_image'),
        mouse: gl.getUniformLocation(this.program, 'u_mouse'),
        time: gl.getUniformLocation(this.program, 'u_time'),
        intensity: gl.getUniformLocation(this.program, 'u_intensity'),
        resolution: gl.getUniformLocation(this.program, 'u_resolution')
      };
    }
    
    setupBuffers() {
      const gl = this.gl;
      
      // Position buffer
      const positionBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
        -1, -1, 1, -1, -1, 1, 1, 1
      ]), gl.STATIC_DRAW);
      
      const positionLocation = gl.getAttribLocation(this.program, 'a_position');
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
      
      // Texture coordinate buffer
      const texCoordBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
        0, 1, 1, 1, 0, 0, 1, 0
      ]), gl.STATIC_DRAW);
      
      const texCoordLocation = gl.getAttribLocation(this.program, 'a_texCoord');
      gl.enableVertexAttribArray(texCoordLocation);
      gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 0, 0);
    }
    
    loadTexture() {
      const gl = this.gl;
      const texture = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, texture);
      
      // Placeholder while image loads
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
        new Uint8Array([26, 26, 26, 255]));
      
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      };
      img.src = this.image.src;
    }
    
    resize() {
      const rect = this.canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = rect.width * dpr;
      this.canvas.height = rect.height * dpr;
      this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    }
    
    setMouse(x, y) {
      this.mouse.targetX = x;
      this.mouse.targetY = y;
    }
    
    setIntensity(value) {
      this.targetIntensity = value;
    }
    
    render() {
      const gl = this.gl;
      if (!gl) return;
      
      // Smooth mouse movement
      this.mouse.x = lerp(this.mouse.x, this.mouse.targetX, 0.1);
      this.mouse.y = lerp(this.mouse.y, this.mouse.targetY, 0.1);
      this.intensity = lerp(this.intensity, this.targetIntensity, 0.08);
      
      this.time += 0.016;
      
      gl.uniform2f(this.uniforms.mouse, this.mouse.x, 1.0 - this.mouse.y);
      gl.uniform1f(this.uniforms.time, this.time);
      gl.uniform1f(this.uniforms.intensity, this.intensity);
      gl.uniform2f(this.uniforms.resolution, this.canvas.width, this.canvas.height);
      
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    
    destroy() {
      if (this.animationId) cancelAnimationFrame(this.animationId);
      if (this.gl) {
        this.gl.getExtension('WEBGL_lose_context')?.loseContext();
      }
    }
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // MAGNETIC CURSOR EFFECT
  // ═══════════════════════════════════════════════════════════════════════════
  
  class MagneticCursor {
    constructor(element) {
      this.element = element;
      this.bounds = null;
      this.position = { x: 0, y: 0 };
      this.target = { x: 0, y: 0 };
      this.isHovering = false;
      this.rafId = null;
      
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.animate = this.animate.bind(this);
    }
    
    init() {
      this.bounds = this.element.getBoundingClientRect();
      this.element.addEventListener('mousemove', this.handleMouseMove);
      this.element.addEventListener('mouseenter', this.handleMouseEnter);
      this.element.addEventListener('mouseleave', this.handleMouseLeave);
      this.animate();
    }
    
    handleMouseMove(e) {
      if (!this.isHovering) return;
      
      const rect = this.element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const distX = e.clientX - centerX;
      const distY = e.clientY - centerY;
      
      this.target.x = distX * CONFIG.magnetic.strength;
      this.target.y = distY * CONFIG.magnetic.strength;
    }
    
    handleMouseEnter() {
      this.isHovering = true;
      this.element.style.transition = 'none';
    }
    
    handleMouseLeave() {
      this.isHovering = false;
      this.target.x = 0;
      this.target.y = 0;
    }
    
    animate() {
      this.position.x = lerp(this.position.x, this.target.x, CONFIG.magnetic.smoothing);
      this.position.y = lerp(this.position.y, this.target.y, CONFIG.magnetic.smoothing);
      
      this.element.style.transform = `translate3d(${this.position.x}px, ${this.position.y}px, 0)`;
      
      this.rafId = requestAnimationFrame(this.animate);
    }
    
    destroy() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.element.removeEventListener('mousemove', this.handleMouseMove);
      this.element.removeEventListener('mouseenter', this.handleMouseEnter);
      this.element.removeEventListener('mouseleave', this.handleMouseLeave);
    }
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 3D TILT EFFECT
  // ═══════════════════════════════════════════════════════════════════════════
  
  class Tilt3D {
    constructor(element) {
      this.element = element;
      this.inner = element.querySelector('.program-expert-card__content');
      this.image = element.querySelector('.program-expert-card__bg-image');
      this.glareElement = null;
      this.rotation = { x: 0, y: 0, targetX: 0, targetY: 0 };
      this.isHovering = false;
      this.rafId = null;
      
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.animate = this.animate.bind(this);
    }
    
    init() {
      // Create glare element
      if (CONFIG.tilt.glare) {
        this.glareElement = document.createElement('div');
        this.glareElement.className = 'program-expert-card__glare';
        this.element.appendChild(this.glareElement);
      }
      
      this.element.style.transformStyle = 'preserve-3d';
      this.element.style.perspective = `${CONFIG.tilt.perspective}px`;
      
      this.element.addEventListener('mousemove', this.handleMouseMove);
      this.element.addEventListener('mouseenter', this.handleMouseEnter);
      this.element.addEventListener('mouseleave', this.handleMouseLeave);
      
      this.animate();
    }
    
    handleMouseMove(e) {
      if (!this.isHovering) return;
      
      const rect = this.element.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      
      this.rotation.targetX = (y - 0.5) * CONFIG.tilt.maxRotation * 2;
      this.rotation.targetY = (x - 0.5) * -CONFIG.tilt.maxRotation * 2;
      
      // Update glare position
      if (this.glareElement) {
        const angle = Math.atan2(y - 0.5, x - 0.5) * (180 / Math.PI);
        this.glareElement.style.background = `
          linear-gradient(
            ${angle + 90}deg,
            rgba(255, 255, 255, ${CONFIG.tilt.glareOpacity}) 0%,
            rgba(255, 255, 255, 0) 80%
          )
        `;
        this.glareElement.style.opacity = '1';
      }
    }
    
    handleMouseEnter() {
      this.isHovering = true;
      this.element.classList.add('is-hovering');
    }
    
    handleMouseLeave() {
      this.isHovering = false;
      this.rotation.targetX = 0;
      this.rotation.targetY = 0;
      this.element.classList.remove('is-hovering');
      
      if (this.glareElement) {
        this.glareElement.style.opacity = '0';
      }
    }
    
    animate() {
      // Spring physics interpolation
      const dx = this.rotation.targetX - this.rotation.x;
      const dy = this.rotation.targetY - this.rotation.y;
      
      this.rotation.x += dx * CONFIG.spring.stiffness;
      this.rotation.y += dy * CONFIG.spring.stiffness;
      
      const scale = this.isHovering ? CONFIG.tilt.scale : 1;
      
      this.element.style.transform = `
        perspective(${CONFIG.tilt.perspective}px)
        rotateX(${this.rotation.x}deg)
        rotateY(${this.rotation.y}deg)
        scale3d(${scale}, ${scale}, ${scale})
      `;
      
      // Parallax on inner elements
      if (this.inner) {
        this.inner.style.transform = `translateZ(30px)`;
      }
      
      if (this.image) {
        this.image.style.transform = `
          translateZ(-10px)
          scale(1.05)
          translateX(${this.rotation.y * -0.5}px)
          translateY(${this.rotation.x * 0.5}px)
        `;
      }
      
      this.rafId = requestAnimationFrame(this.animate);
    }
    
    destroy() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.element.removeEventListener('mousemove', this.handleMouseMove);
      this.element.removeEventListener('mouseenter', this.handleMouseEnter);
      this.element.removeEventListener('mouseleave', this.handleMouseLeave);
      if (this.glareElement) this.glareElement.remove();
    }
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SCROLL VELOCITY ANIMATION
  // ═══════════════════════════════════════════════════════════════════════════
  
  class ScrollVelocityEffect {
    constructor(track) {
      this.track = track;
      this.velocity = 0;
      this.lastScrollY = window.scrollY;
      this.baseSpeed = parseFloat(getComputedStyle(track).animationDuration) || 60;
      this.rafId = null;
      
      this.handleScroll = this.handleScroll.bind(this);
      this.animate = this.animate.bind(this);
    }
    
    init() {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.animate();
    }
    
    handleScroll() {
      const currentScrollY = window.scrollY;
      this.velocity = Math.abs(currentScrollY - this.lastScrollY);
      this.lastScrollY = currentScrollY;
    }
    
    animate() {
      // Slow down carousel when scrolling fast, speed up when stopped
      const targetDuration = this.baseSpeed + (this.velocity * 0.5);
      const currentDuration = parseFloat(this.track.style.animationDuration) || this.baseSpeed;
      const newDuration = lerp(currentDuration, targetDuration, 0.1);
      
      this.track.style.animationDuration = `${newDuration}s`;
      
      // Decay velocity
      this.velocity *= 0.95;
      
      this.rafId = requestAnimationFrame(this.animate);
    }
    
    destroy() {
      window.removeEventListener('scroll', this.handleScroll);
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // LIGHT TRAIL CURSOR
  // ═══════════════════════════════════════════════════════════════════════════
  
  class LightTrailCursor {
    constructor(container) {
      this.container = container;
      this.particles = [];
      this.maxParticles = 20;
      this.isActive = false;
      this.lastMouse = { x: 0, y: 0 };
      this.rafId = null;
      
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.animate = this.animate.bind(this);
    }
    
    init() {
      // Create particle pool
      for (let i = 0; i < this.maxParticles; i++) {
        const particle = document.createElement('div');
        particle.className = 'program-hero__light-particle';
        particle.style.cssText = `
          position: absolute;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(238, 184, 122, 0.8) 0%, rgba(238, 184, 122, 0) 70%);
          pointer-events: none;
          opacity: 0;
          transform: translate(-50%, -50%);
          z-index: 1000;
          mix-blend-mode: screen;
        `;
        this.container.appendChild(particle);
        this.particles.push({
          element: particle,
          x: 0,
          y: 0,
          life: 0,
          maxLife: 30 + Math.random() * 20,
          size: 4 + Math.random() * 8
        });
      }
      
      this.container.addEventListener('mousemove', this.handleMouseMove);
      this.container.addEventListener('mouseenter', this.handleMouseEnter);
      this.container.addEventListener('mouseleave', this.handleMouseLeave);
      
      this.animate();
    }
    
    handleMouseMove(e) {
      if (!this.isActive) return;
      
      const rect = this.container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Only spawn if mouse moved enough
      const dist = Math.hypot(x - this.lastMouse.x, y - this.lastMouse.y);
      if (dist > 5) {
        this.spawnParticle(x, y);
        this.lastMouse.x = x;
        this.lastMouse.y = y;
      }
    }
    
    handleMouseEnter() {
      this.isActive = true;
    }
    
    handleMouseLeave() {
      this.isActive = false;
    }
    
    spawnParticle(x, y) {
      // Find dead particle to reuse
      const particle = this.particles.find(p => p.life <= 0);
      if (!particle) return;
      
      particle.x = x;
      particle.y = y;
      particle.life = particle.maxLife;
    }
    
    animate() {
      this.particles.forEach(p => {
        if (p.life > 0) {
          p.life--;
          const progress = p.life / p.maxLife;
          const size = p.size * progress;
          
          p.element.style.left = `${p.x}px`;
          p.element.style.top = `${p.y}px`;
          p.element.style.width = `${size}px`;
          p.element.style.height = `${size}px`;
          p.element.style.opacity = progress * 0.6;
        } else {
          p.element.style.opacity = '0';
        }
      });
      
      this.rafId = requestAnimationFrame(this.animate);
    }
    
    destroy() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.container.removeEventListener('mousemove', this.handleMouseMove);
      this.container.removeEventListener('mouseenter', this.handleMouseEnter);
      this.container.removeEventListener('mouseleave', this.handleMouseLeave);
      this.particles.forEach(p => p.element.remove());
    }
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // GSAP REVEAL ANIMATIONS - SIMPLIFIED
  // Only animate carousels and highlight word for cleaner page load
  // ═══════════════════════════════════════════════════════════════════════════
  
  function initGSAPAnimations() {
    if (typeof gsap === 'undefined') {
      console.warn('GSAP not loaded, skipping reveal animations');
      return;
    }
    
    gsap.registerPlugin(ScrollTrigger);
    
    const hero = document.querySelector('.program-hero');
    if (!hero) return;
    
    // Simplified timeline - only carousel and highlight animations
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: hero,
        start: 'top 80%',
        end: 'top 20%',
        toggleActions: 'play none none reverse'
      }
    });
    
    // Highlight word 'best' - elegant reveal with slight delay
    tl.from('.program-hero__heading-highlight', {
      opacity: 0,
      scale: 1.1,
      filter: 'blur(6px)',
      duration: 0.9,
      ease: 'power3.out'
    }, 0.3);
    
    // Carousel rows with opposing slide directions - the main entrance effect
    tl.from('.program-hero__carousel-row--1', {
      opacity: 0,
      x: -80,
      duration: 1,
      ease: 'power4.out'
    }, 0.2);
    
    tl.from('.program-hero__carousel-row--2', {
      opacity: 0,
      x: 80,
      duration: 1,
      ease: 'power4.out'
    }, 0.35);
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════
  
  class ProgramHeroPremium {
    constructor() {
      this.effects = [];
      this.webglInstances = [];
      this.isInitialized = false;
    }
    
    init() {
      if (this.isInitialized) return;
      
      const hero = document.querySelector('.program-hero');
      if (!hero) return;
      
      const mobile = isMobile();
      const reducedMotion = isReducedMotion();
      
      console.log('🎨 Program Hero Premium: Initializing enterprise-grade experience');
      
      // Skip heavy effects on mobile or reduced motion
      if (reducedMotion) {
        console.log('Reduced motion detected, using minimal animations');
        return;
      }
      
      // Initialize GSAP reveal animations
      initGSAPAnimations();
      
      // Desktop-only premium effects
      if (!mobile) {
        // Initialize 3D tilt on cards
        const cards = document.querySelectorAll('.program-expert-card');
        cards.forEach(card => {
          const tilt = new Tilt3D(card);
          tilt.init();
          this.effects.push(tilt);
        });
        
        // Initialize light trail cursor
        const carouselWrapper = document.querySelector('.program-hero__carousel-wrapper');
        if (carouselWrapper) {
          const lightTrail = new LightTrailCursor(carouselWrapper);
          lightTrail.init();
          this.effects.push(lightTrail);
        }
        
        // NOTE: Scroll velocity effect disabled - was causing clunky snapping behavior
        // The carousel now uses smooth CSS-only animation for best performance
        
        // Magnetic button effect disabled - felt too gimmicky
        // const buttons = document.querySelectorAll('.program-hero__btn');
        // buttons.forEach(btn => {
        //   const magnetic = new MagneticCursor(btn);
        //   magnetic.init();
        //   this.effects.push(magnetic);
        // });
        
        // NOTE: WebGL card effects disabled - too many contexts causes browser issues
        // The 3D tilt, magnetic cursor, and light trails provide the premium feel
        // without the performance overhead of multiple WebGL contexts
      }
      
      // Add premium class for CSS enhancements
      hero.classList.add('program-hero--premium-enabled');
      
      this.isInitialized = true;
      console.log('✨ Program Hero Premium: Initialization complete');
    }
    
    initWebGLCards(cards) {
      cards.forEach((card, index) => {
        const image = card.querySelector('.program-expert-card__bg-image');
        if (!image) return;
        
        // Create canvas overlay
        const canvas = document.createElement('canvas');
        canvas.className = 'program-expert-card__webgl-canvas';
        canvas.style.cssText = `
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          z-index: 2;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.5s ease;
        `;
        card.appendChild(canvas);
        
        const webgl = new WebGLDistortion(canvas, image);
        if (webgl.init()) {
          this.webglInstances.push(webgl);
          
          // Show WebGL canvas on hover
          card.addEventListener('mouseenter', () => {
            canvas.style.opacity = '1';
            webgl.setIntensity(1);
          });
          
          card.addEventListener('mouseleave', () => {
            canvas.style.opacity = '0';
            webgl.setIntensity(0);
          });
          
          card.addEventListener('mousemove', (e) => {
            const rect = card.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width;
            const y = (e.clientY - rect.top) / rect.height;
            webgl.setMouse(x, y);
          });
        }
      });
      
      // Render loop for all WebGL instances
      const renderLoop = () => {
        this.webglInstances.forEach(instance => instance.render());
        requestAnimationFrame(renderLoop);
      };
      renderLoop();
    }
    
    destroy() {
      this.effects.forEach(effect => effect.destroy?.());
      this.webglInstances.forEach(instance => instance.destroy());
      this.effects = [];
      this.webglInstances = [];
      this.isInitialized = false;
    }
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // AUTO-INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════
  
  const premiumHero = new ProgramHeroPremium();
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => premiumHero.init());
  } else {
    // Small delay to ensure all resources are loaded
    requestAnimationFrame(() => premiumHero.init());
  }
  
  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('.program-hero')) {
      premiumHero.destroy();
      premiumHero.init();
    }
  });
  
  // Handle visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      premiumHero.init();
    }
  });
  
  // Expose for debugging
  window.ProgramHeroPremium = premiumHero;
  
})();

